import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { Users, UserPlus, Trash2, Shield, ShieldCheck, Eye, EyeOff, Pencil, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminManagement() {
  const { language } = useLanguage();
  const { isSuperAdmin, getAuthHeaders, admin: currentAdmin } = useAuth();
  
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  
  // Edit state
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: '',
    role: 'club_admin'
  });

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    password: '',
    role: ''
  });

  // Fetch admins list
  const fetchAdmins = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/admins`, {
        headers: getAuthHeaders()
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      toast.error(language === 'el' ? 'Αποτυχία φόρτωσης' : 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, language]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdmins();
    }
  }, [isSuperAdmin, fetchAdmins]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      toast.error(language === 'el' ? 'Συμπληρώστε όλα τα πεδία' : 'Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/admins`,
        newAdmin,
        { headers: getAuthHeaders() }
      );
      
      toast.success(language === 'el' ? 'Admin δημιουργήθηκε' : 'Admin created');
      setNewAdmin({ name: '', email: '', password: '', role: 'club_admin' });
      setShowAddForm(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (admin) => {
    setEditingAdmin(admin);
    setEditForm({
      name: admin.name,
      email: admin.email,
      password: '', // Empty - only set if user wants to change it
      role: admin.role
    });
    setShowEditPassword(false);
  };

  const handleEditSave = async () => {
    if (!editForm.name || !editForm.email) {
      toast.error(language === 'el' ? 'Όνομα και email απαιτούνται' : 'Name and email are required');
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        name: editForm.name,
        email: editForm.email,
      };
      
      // Only include password if user entered a new one
      if (editForm.password && editForm.password.trim()) {
        updateData.password = editForm.password;
      }
      
      // Only include role if it changed and it's not the current user
      if (editForm.role !== editingAdmin.role && editingAdmin.id !== currentAdmin?.id) {
        updateData.role = editForm.role;
      }

      await axios.put(
        `${API_URL}/api/auth/admins/${editingAdmin.id}`,
        updateData,
        { headers: getAuthHeaders() }
      );
      
      toast.success(language === 'el' ? 'Admin ενημερώθηκε' : 'Admin updated');
      setEditingAdmin(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;
    
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/auth/admins/${adminToDelete.id}`,
        { headers: getAuthHeaders() }
      );
      
      toast.success(language === 'el' ? 'Admin διαγράφηκε' : 'Admin deleted');
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (adminId, newRole) => {
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/auth/admins/${adminId}/role?role=${newRole}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(language === 'el' ? 'Ρόλος ενημερώθηκε' : 'Role updated');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">
          {language === 'el' ? 'Δεν έχετε πρόσβαση' : 'Access Denied'}
        </h2>
        <p className="text-muted-foreground">
          {language === 'el' 
            ? 'Μόνο οι Super Admins μπορούν να διαχειρίζονται admins.'
            : 'Only Super Admins can manage admins.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">
              {language === 'el' ? 'Διαχείριση Admins' : 'Admin Management'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {language === 'el' ? 'Προσθήκη και διαχείριση διαχειριστών' : 'Add and manage administrators'}
            </p>
          </div>
        </div>
        
        <Button onClick={() => setShowAddForm(!showAddForm)} className="gap-2">
          <UserPlus className="h-4 w-4" />
          {language === 'el' ? 'Νέος Admin' : 'New Admin'}
        </Button>
      </div>

      {/* Add Admin Form */}
      {showAddForm && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {language === 'el' ? 'Προσθήκη Νέου Admin' : 'Add New Admin'}
          </h3>
          
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Όνομα' : 'Name'}</Label>
                <Input
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Admin Name"
                  data-testid="new-admin-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="admin@club.com"
                  data-testid="new-admin-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Κωδικός' : 'Password'}</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                    className="pr-10"
                    data-testid="new-admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Ρόλος' : 'Role'}</Label>
                <Select 
                  value={newAdmin.role} 
                  onValueChange={(value) => setNewAdmin(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="new-admin-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="club_admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Club Admin
                      </div>
                    </SelectItem>
                    <SelectItem value="super_admin">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        Super Admin
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Role explanation */}
            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="font-medium mb-2">{language === 'el' ? 'Επεξήγηση ρόλων:' : 'Role explanation:'}</p>
              <ul className="space-y-1 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <strong>Club Admin:</strong> {language === 'el' ? 'Παίκτες, αγώνες' : 'Players, matches only'}
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  <strong>Super Admin:</strong> {language === 'el' ? 'Πλήρη δικαιώματα, branding, admins' : 'Full access, branding, admin management'}
                </li>
              </ul>
            </div>
            
            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? '...' : (language === 'el' ? 'Δημιουργία' : 'Create')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                {language === 'el' ? 'Ακύρωση' : 'Cancel'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Admin Modal */}
      {editingAdmin && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border border-border p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Pencil className="h-5 w-5" />
                {language === 'el' ? 'Επεξεργασία Admin' : 'Edit Admin'}
              </h3>
              <button 
                onClick={() => setEditingAdmin(null)}
                className="p-1 rounded hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Όνομα' : 'Name'}</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Admin Name"
                  data-testid="edit-admin-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="admin@club.com"
                  data-testid="edit-admin-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label>
                  {language === 'el' ? 'Νέος Κωδικός' : 'New Password'}
                  <span className="text-muted-foreground font-normal ml-1">
                    ({language === 'el' ? 'κενό = χωρίς αλλαγή' : 'empty = no change'})
                  </span>
                </Label>
                <div className="relative">
                  <Input
                    type={showEditPassword ? 'text' : 'password'}
                    value={editForm.password}
                    onChange={(e) => setEditForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                    className="pr-10"
                    data-testid="edit-admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showEditPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Ρόλος' : 'Role'}</Label>
                <Select 
                  value={editForm.role} 
                  onValueChange={(value) => setEditForm(prev => ({ ...prev, role: value }))}
                  disabled={editingAdmin.id === currentAdmin?.id}
                >
                  <SelectTrigger data-testid="edit-admin-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="club_admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Club Admin
                      </div>
                    </SelectItem>
                    <SelectItem value="super_admin">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        Super Admin
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {editingAdmin.id === currentAdmin?.id && (
                  <p className="text-xs text-muted-foreground">
                    {language === 'el' 
                      ? 'Δεν μπορείτε να αλλάξετε τον δικό σας ρόλο'
                      : 'You cannot change your own role'}
                  </p>
                )}
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={handleEditSave} disabled={loading} className="flex-1">
                  {loading ? '...' : (language === 'el' ? 'Αποθήκευση' : 'Save')}
                </Button>
                <Button variant="outline" onClick={() => setEditingAdmin(null)} className="flex-1">
                  {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admins List */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border bg-muted/30">
          <h3 className="font-semibold">
            {language === 'el' ? 'Λίστα Admins' : 'Admins List'} ({admins.length})
          </h3>
        </div>
        
        {loading && admins.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Loading...
          </div>
        ) : admins.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {language === 'el' ? 'Δεν υπάρχουν admins' : 'No admins found'}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {admins.map((admin) => (
              <div 
                key={admin.id} 
                className="p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
                data-testid={`admin-row-${admin.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {admin.role === ROLES.SUPER_ADMIN ? (
                      <ShieldCheck className="h-5 w-5 text-primary" />
                    ) : (
                      <Shield className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{admin.name}</p>
                    <p className="text-sm text-muted-foreground">{admin.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Role badge / selector */}
                  {admin.id === currentAdmin?.id ? (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {admin.role === ROLES.SUPER_ADMIN ? 'Super Admin' : 'Club Admin'}
                      <span className="ml-1 text-muted-foreground">(you)</span>
                    </span>
                  ) : (
                    <Select 
                      value={admin.role} 
                      onValueChange={(value) => handleRoleChange(admin.id, value)}
                    >
                      <SelectTrigger className="w-[140px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="club_admin">Club Admin</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  
                  {/* Edit button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditClick(admin)}
                    data-testid={`edit-admin-${admin.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  
                  {/* Delete button (not for self) */}
                  {admin.id !== currentAdmin?.id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        setAdminToDelete(admin);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-admin-${admin.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'el' ? 'Διαγραφή Admin' : 'Delete Admin'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'el'
                ? `Είστε σίγουροι ότι θέλετε να διαγράψετε τον ${adminToDelete?.name};`
                : `Are you sure you want to delete ${adminToDelete?.name}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'el' ? 'Ακύρωση' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAdmin}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {language === 'el' ? 'Διαγραφή' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
